import { CopyOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Grid,
  Input,
  InputNumber,
  Modal,
  message,
  Row,
  Select,
  Tooltip,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import { CrmTagSelector } from '@/modules/zy-crm/components/crm-tags';
import { CustomerEditModal } from '@/modules/zy-crm/components/customer-edit-modal';
import { useDuplicateNameCheck } from '@/modules/zy-crm/components/duplicate-name-check';
import { FormSectionDivider } from '@/modules/zy-crm/components/form-section-divider';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import {
  ProductCenterFormSection,
  type ProductCenterRow,
  type ProductCenterSelection,
} from '@/modules/zy-crm/components/product-center-form-section';
import {
  queryAllTargetReferrals,
  type ReferralFormItem,
  ReferralFormSection,
  syncTargetReferrals,
} from '@/modules/zy-crm/components/referral-form-section';
import {
  ResourceAreaSelector,
  type ResourceAreaValue,
} from '@/modules/zy-crm/components/resource-area-selector';
import { useMemberDirectory } from '@/modules/zy-crm/member-directory';
import { resolveCrmScopedViewModeFromPathname } from '@/modules/zy-crm/utils/crm-view';
import { toApiDateTime } from '@/modules/zy-crm/utils/display';
import { createSelectedMemberAwareAdapter } from '@/modules/zy-crm/utils/member-picker-adapter';
import { getResponsiveModalWidth } from '@/modules/zy-crm/utils/modal-width';
import { OPPORTUNITY_PRIORITY_OPTIONS } from '@/modules/zy-crm/utils/opportunity-priority';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import {
  fetchCustomerOptions,
  fetchEnabledPipelineOptions,
  fetchStageOptions,
  mergeOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import {
  queryOpportunityDetail,
  queryOpportunityDrawerProfile,
} from '@/services/bff/opportunity.api';
import { replaceEntityTags } from '@/services/bff/taxonomy-management.api';
import type {
  OpportunityItem,
  OpportunityPriority,
} from '@/services/bff/types/opportunity';
import type { ReferralItem } from '@/services/bff/types/referral';
import { queryTaxonomyTreeByIdentifier } from '@/services/mp/taxonomy';
import { useOpenPlatformConfig } from '@/services/openPlatformToken';
import { createAppRequest } from '@/services/request';
import { getCurrentMemberUuid } from '@/utils/currentMember';
import {
  type AreaCascaderOption,
  type AreaUuidPathMap,
  buildAreaCascaderData,
} from '@/utils/taxonomy';

const request = createAppRequest();
const TAXONOMY_AREA = 'CnAdministrativeDivision';
const DEFAULT_PIPELINE_NAME = '标准销售流程';
const NEW_PRODUCT_ROW_KEY = '__new_product__';
const MemberPickerField = MemberPicker as unknown as React.FC<any>;

function getCurrentPathname() {
  if (typeof window === 'undefined') {
    return '';
  }
  return (
    (window.location.pathname || '') +
    (window.location.pathname.endsWith('/overview')
      ? window.location.search || ''
      : '')
  );
}

type OpportunityProductRowMeta = {
  uuid?: string;
};

type OpportunityProductDraftRow = ProductCenterRow &
  OpportunityProductRowMeta & {
    isNew?: boolean;
  };

export type OpportunityFormValues = {
  name?: string;
  customerUuid?: string;
  ownerUserUuid?: string;
  pipelineUuid?: string;
  stageUuid?: string;
  priority?: OpportunityPriority;
  areas?: ResourceAreaValue[];
  totalPrice?: number;
  quotedPrice?: number;
  dealPrice?: number;
  expectedDealDate?: Dayjs;
  dealTime?: Dayjs;
  contactNextTime?: Dayjs;
  discountPercent?: number;
  remark?: string;
  referrals?: ReferralFormItem[];
  currentBlockers?: string;
  decisionMakerStatus?: string;
  budgetStatus?: string;
  competitorInfo?: string;
  riskTags?: string[];
  tagUuids?: string[];
};

type OpportunityEditModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width?: number | string;
  opportunityUuid?: string;
  initialValues?: Partial<OpportunityFormValues>;
  context?: {
    customerUuid?: string;
    customerName?: string;
    ownerUserUuid?: string;
  };
  currentMemberUuid?: string | null;
  zIndex?: number;
  /** Called after each successful creation, including save-and-continue. */
  onCreated?: (record: Pick<OpportunityItem, 'uuid' | 'name'>) => void;
  onSuccess: (
    record?: Pick<OpportunityItem, 'uuid' | 'name'>,
  ) => void | Promise<void>;
};

const normalizeText = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const BUDGET_STATUS_OPTIONS = [
  { label: '预算已明确', value: '预算已明确' },
  { label: '未明确', value: '未明确' },
  { label: '未知', value: '未知' },
];

const DECISION_MAKER_STATUS_OPTIONS = [
  { label: '已触达', value: '已触达' },
  { label: '未触达', value: '未触达' },
  { label: '不确定', value: '不确定' },
];

const parseRiskTagsFromString = (value?: string | null): string[] => {
  const raw = String(value || '').trim();
  if (!raw) return [];
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item: unknown) => String(item || '').trim())
          .filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  return raw
    .split(/\r?\n|,|，/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const toNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return 0;
  }
  const next = Number(value);
  return Number.isFinite(next) ? next : 0;
};

const toAmount = (value: unknown, precision = 2) =>
  toNumber(value).toFixed(precision);

const toDateValue = (value?: string | null) =>
  value ? dayjs(value) : undefined;

const resolveDefaultPipelineOption = (options: RelationOption[]) =>
  options.find(
    (item) =>
      Number(
        (item.raw as Record<string, unknown> | undefined)?.isDefault || 0,
      ) > 0,
  ) ||
  options.find(
    (item) => String(item.label || '').trim() === DEFAULT_PIPELINE_NAME,
  );

const normalizeOpportunitySharedRow = (
  row?: Partial<ProductCenterRow>,
): ProductCenterRow => {
  const standardPrice = toNumber(row?.standardPrice);
  const opportunityPrice =
    row?.opportunityPrice === undefined
      ? standardPrice
      : toNumber(row.opportunityPrice);
  const quantity = toNumber(row?.quantity || 1);
  return {
    key: String(row?.key || '').trim() || NEW_PRODUCT_ROW_KEY,
    uuid: normalizeText(row?.uuid) || undefined,
    offeringUuid: normalizeText(row?.offeringUuid) || undefined,
    offeringVersionUuid: normalizeText(row?.offeringVersionUuid) || undefined,
    productName: normalizeText(row?.productName) || undefined,
    productCode: normalizeText(row?.productCode) || undefined,
    productType: normalizeText(row?.productType) || undefined,
    productUnit: normalizeText(row?.productUnit) || undefined,
    versionNo: normalizeText(row?.versionNo) || undefined,
    standardPrice: toAmount(standardPrice),
    selectionSnapshot: row?.selectionSnapshot,
    opportunityPrice: toAmount(opportunityPrice),
    quantity: toAmount(quantity),
    totalPrice: toAmount(opportunityPrice * quantity),
    rowSort: row?.rowSort,
    remark: normalizeText(row?.remark) || undefined,
  };
};

const normalizeOpportunitySharedRows = (rows: ProductCenterRow[]) =>
  rows.map((item) => normalizeOpportunitySharedRow(item));

const fromOpportunityApiRow = (
  row?: Partial<ProductCenterRow>,
): ProductCenterRow => {
  const quantity = toNumber(row?.quantity || 1);
  const opportunityPrice = toNumber(row?.opportunityPrice);
  return {
    key: String(row?.key || '').trim() || NEW_PRODUCT_ROW_KEY,
    uuid: normalizeText(row?.uuid) || undefined,
    offeringUuid: normalizeText(row?.offeringUuid) || undefined,
    offeringVersionUuid: normalizeText(row?.offeringVersionUuid) || undefined,
    productName: normalizeText(row?.productName) || undefined,
    productCode: normalizeText(row?.productCode) || undefined,
    productType: normalizeText(row?.productType) || undefined,
    productUnit: normalizeText(row?.productUnit) || undefined,
    versionNo: normalizeText(row?.versionNo) || undefined,
    standardPrice: toAmount(row?.standardPrice),
    selectionSnapshot: row?.selectionSnapshot,
    opportunityPrice: toAmount(opportunityPrice),
    quantity: toAmount(quantity),
    totalPrice: toAmount(opportunityPrice * quantity),
    rowSort: row?.rowSort,
    remark: normalizeText(row?.remark) || undefined,
  };
};

const toOpportunityProductMeta = (
  row?: Partial<ProductCenterRow>,
): OpportunityProductRowMeta => ({
  uuid: normalizeText(row?.uuid) || undefined,
});

const buildProductPayload = (
  row: ProductCenterRow,
  opportunityUuid: string,
) => {
  const quantity = Math.max(0, toNumber(row.quantity));
  const opportunityPrice = toNumber(row.opportunityPrice);
  return {
    opportunityUuid,
    offeringUuid: row.offeringUuid,
    offeringVersionUuid: row.offeringVersionUuid,
    productName: normalizeText(row.productName) || undefined,
    productCode: normalizeText(row.productCode) || undefined,
    productType: normalizeText(row.productType) || undefined,
    productUnit: normalizeText(row.productUnit) || undefined,
    versionNo: normalizeText(row.versionNo) || undefined,
    standardPrice: toAmount(row.standardPrice, 6),
    selectionSnapshot: row.selectionSnapshot,
    opportunityPrice: toAmount(opportunityPrice, 6),
    quantity: toAmount(quantity, 6),
    totalPrice: toAmount(opportunityPrice * quantity, 6),
    rowSort: row.rowSort,
    remark: normalizeText(row.remark) || undefined,
  };
};

const ensureRelationOption = (
  options: RelationOption[],
  value?: string,
  label?: string,
) => {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return options;
  }
  if (options.some((item) => item.value === normalizedValue)) {
    return options;
  }
  return [
    {
      value: normalizedValue,
      label: normalizeText(label) || normalizedValue,
    },
    ...options,
  ];
};

export function OpportunityEditModal({
  open,
  onOpenChange,
  width,
  opportunityUuid,
  initialValues,
  context,
  currentMemberUuid,
  zIndex,
  onCreated,
  onSuccess,
}: OpportunityEditModalProps) {
  const pageViewMode = resolveCrmScopedViewModeFromPathname(
    getCurrentPathname(),
    'opportunities',
    { allowPool: true },
  );
  const [form] = Form.useForm<OpportunityFormValues>();
  const duplicateCheck = useDuplicateNameCheck({
    entity: 'opportunity',
    form,
    enabled: open && !opportunityUuid,
  });
  const screens = Grid.useBreakpoint();
  const isEdit = Boolean(opportunityUuid);
  const [submitting, setSubmitting] = useState(false);
  const [detailLoadFailed, setDetailLoadFailed] = useState(false);
  const [memberLoadFailed, setMemberLoadFailed] = useState(false);
  const [relationLoading, setRelationLoading] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<RelationOption[]>([]);
  const [pipelineOptions, setPipelineOptions] = useState<RelationOption[]>([]);
  const [areaOptions, setAreaOptions] = useState<AreaCascaderOption[]>([]);
  const [areaUuidPathMap, setAreaUuidPathMap] = useState<AreaUuidPathMap>({});
  const [pipelineConfigMissing, setPipelineConfigMissing] = useState(false);
  const [stageOptions, setStageOptions] = useState<RelationOption[]>([]);
  const [stageLoading, setStageLoading] = useState(false);
  const [customerCreateOpen, setCustomerCreateOpen] = useState(false);
  const [productRows, setProductRows] = useState<ProductCenterRow[]>([]);
  const [editingProductKey, setEditingProductKey] = useState<string>();
  const [productDraftRow, setProductDraftRow] =
    useState<OpportunityProductDraftRow>();
  const [productRowMeta, setProductRowMeta] = useState<
    Record<string, OpportunityProductRowMeta>
  >({});
  const [initialReferrals, setInitialReferrals] = useState<ReferralItem[]>([]);
  const [existingProductRows, setExistingProductRows] = useState<
    Array<ProductCenterRow & OpportunityProductRowMeta>
  >([]);
  const { searchMembers } = useMemberDirectory();
  const productCenter = useOpenPlatformConfig();
  const selectedOwnerUserUuid = Form.useWatch('ownerUserUuid', form);
  const ownerMemberAdapter = useMemo(
    () =>
      createSelectedMemberAwareAdapter(
        orgDirectoryAdapter,
        selectedOwnerUserUuid,
      ),
    [selectedOwnerUserUuid],
  );

  const modalWidth = width ?? getResponsiveModalWidth('wideForm');

  const lockCustomer = !isEdit && Boolean(normalizeText(context?.customerUuid));

  const resetState = () => {
    form.resetFields();
    setDetailLoadFailed(false);
    setStageOptions([]);
    setStageLoading(false);
    setProductRows([]);
    setExistingProductRows([]);
    setProductRowMeta({});
    setInitialReferrals([]);
    setEditingProductKey(undefined);
    setProductDraftRow(undefined);
  };

  const loadMembers = async (keyword = '', ensureValue?: string) => {
    setMemberLoadFailed(false);
    try {
      await searchMembers(keyword || undefined, {
        ensureValue: String(ensureValue || '').trim() || undefined,
      });
    } catch {
      setMemberLoadFailed(true);
      message.error('成员列表加载失败，请稍后重试');
    }
  };

  const loadRelationOptions = async (
    keyword?: string,
    ensureValues?: {
      customerUuid?: string;
      pipelineUuid?: string;
      pipelineName?: string;
    },
    searchTarget?: 'customer' | 'pipeline',
  ) => {
    setRelationLoading(true);
    try {
      const [customers, pipelines, areaTree] = await Promise.all([
        fetchCustomerOptions(searchTarget === 'pipeline' ? undefined : keyword),
        fetchEnabledPipelineOptions(
          searchTarget === 'customer' ? undefined : keyword,
        ),
        queryTaxonomyTreeByIdentifier(TAXONOMY_AREA),
      ]);
      const areaTreeData = buildAreaCascaderData(areaTree.tags);
      const currentCustomerUuid = String(
        ensureValues?.customerUuid || form.getFieldValue('customerUuid') || '',
      ).trim();
      const currentPipelineUuid = String(
        ensureValues?.pipelineUuid || form.getFieldValue('pipelineUuid') || '',
      ).trim();
      const currentPipelineLabel = pipelineOptions.find(
        (item) => item.value === currentPipelineUuid,
      )?.label;
      const currentCustomerLabel = customerOptions.find(
        (item) => item.value === currentCustomerUuid,
      )?.label;
      const nextPipelineOptions = ensureRelationOption(
        mergeOptions([], pipelines),
        currentPipelineUuid,
        ensureValues?.pipelineName || currentPipelineLabel,
      );
      const nextCustomerOptions = ensureRelationOption(
        mergeOptions([], customers),
        currentCustomerUuid,
        currentCustomerUuid === normalizeText(context?.customerUuid)
          ? context?.customerName
          : currentCustomerLabel,
      );
      setCustomerOptions(nextCustomerOptions);
      setPipelineOptions(nextPipelineOptions);
      setAreaOptions(areaTreeData.options);
      setAreaUuidPathMap(areaTreeData.uuidPathMap);
      setPipelineConfigMissing(nextPipelineOptions.length === 0);

      if (!currentPipelineUuid) {
        const defaultPipeline =
          resolveDefaultPipelineOption(nextPipelineOptions);
        if (defaultPipeline?.value) {
          form.setFieldValue('pipelineUuid', defaultPipeline.value);
        } else {
          form.setFieldValue('pipelineUuid', undefined);
        }
      }
      const resolvedPipelineUuid =
        currentPipelineUuid || form.getFieldValue('pipelineUuid') || undefined;
      if (resolvedPipelineUuid) {
        void loadStageOptions(String(resolvedPipelineUuid));
      }
    } finally {
      setRelationLoading(false);
    }
  };

  const loadStageOptions = async (pipelineUuid?: string) => {
    if (!pipelineUuid) {
      setStageOptions([]);
      return;
    }
    setStageLoading(true);
    try {
      const options = await fetchStageOptions(undefined, pipelineUuid);
      setStageOptions(options);
    } catch {
      setStageOptions([]);
    } finally {
      setStageLoading(false);
    }
  };

  const handleProductSelected = (
    row: ProductCenterRow,
    selection: ProductCenterSelection,
  ) => {
    setProductDraftRow((current) =>
      current && current.key === row.key
        ? normalizeOpportunitySharedRow({
            ...current,
            ...selection,
            opportunityPrice: selection.standardPrice,
          })
        : current,
    );
  };

  const stopProductEditing = () => {
    setEditingProductKey(undefined);
    setProductDraftRow(undefined);
  };

  const addProductRow = () => {
    if (editingProductKey) {
      message.warning('请先保存或取消当前编辑的产品');
      return;
    }
    setEditingProductKey(NEW_PRODUCT_ROW_KEY);
    setProductDraftRow(
      normalizeOpportunitySharedRow({
        key: NEW_PRODUCT_ROW_KEY,
        quantity: '1',
      }),
    );
  };

  const beginEditProductRow = (record: ProductCenterRow) => {
    if (editingProductKey && editingProductKey !== record.key) {
      message.warning('请先保存或取消当前编辑的产品');
      return;
    }
    setEditingProductKey(record.key);
    setProductDraftRow({
      ...normalizeOpportunitySharedRow(record),
      ...(productRowMeta[record.key] || {}),
      isNew: false,
    });
  };

  const handleProductDraftChange = (
    patch:
      | Partial<OpportunityProductDraftRow>
      | ((
          current: OpportunityProductDraftRow,
        ) => Partial<OpportunityProductDraftRow>),
  ) => {
    setProductDraftRow((current) => {
      const currentMeta = (current ||
        {}) as Partial<OpportunityProductDraftRow>;
      const base = {
        ...normalizeOpportunitySharedRow(current),
        uuid: currentMeta?.uuid,
        isNew: currentMeta?.isNew,
      } as OpportunityProductDraftRow;
      const nextPatch = typeof patch === 'function' ? patch(base) : patch;
      return {
        ...normalizeOpportunitySharedRow({
          ...base,
          ...nextPatch,
          key: base.key,
        }),
        uuid: base.uuid,
        isNew: base.isNew,
      };
    });
  };

  const saveProductRow = () => {
    if (!productDraftRow || !editingProductKey) {
      return;
    }
    if (!String(productDraftRow.offeringVersionUuid || '').trim()) {
      message.error('请选择产品');
      return;
    }
    const nextRow = normalizeOpportunitySharedRow({
      ...productDraftRow,
      key:
        editingProductKey === NEW_PRODUCT_ROW_KEY
          ? `${Date.now()}-${Math.random()}`
          : editingProductKey,
    });
    const nextMeta = {
      uuid: productDraftRow.uuid,
    };
    if (editingProductKey === NEW_PRODUCT_ROW_KEY) {
      setProductRows((prev) => [...prev, nextRow]);
      setProductRowMeta((prev) => ({ ...prev, [nextRow.key]: nextMeta }));
    } else {
      setProductRows((prev) =>
        normalizeOpportunitySharedRows(
          prev.map((item) => (item.key === editingProductKey ? nextRow : item)),
        ),
      );
      setProductRowMeta((prev) => ({ ...prev, [nextRow.key]: nextMeta }));
    }
    stopProductEditing();
  };

  const deleteProductRow = (record: ProductCenterRow) => {
    if (record.key === NEW_PRODUCT_ROW_KEY) {
      stopProductEditing();
      return;
    }
    setProductRows((prev) => prev.filter((item) => item.key !== record.key));
    setProductRowMeta((prev) => {
      const next = { ...prev };
      delete next[record.key];
      return next;
    });
    if (editingProductKey === record.key) {
      stopProductEditing();
    }
  };

  const loadEditData = async () => {
    if (!opportunityUuid) return;
    try {
      const [record, profile, referrals] = await Promise.all([
        queryOpportunityDetail(opportunityUuid, { viewMode: pageViewMode }),
        queryOpportunityDrawerProfile(opportunityUuid, {
          viewMode: pageViewMode,
        }),
        queryAllTargetReferrals({
          targetType: 'opportunity',
          targetUuid: opportunityUuid,
        }),
      ]);
      form.resetFields();
      form.setFieldsValue({
        name: record?.name,
        customerUuid: record?.customerUuid,
        ownerUserUuid: record?.ownerUserUuid,
        pipelineUuid: record?.pipelineUuid,
        stageUuid: record?.stageUuid,
        priority: record?.priority || 'medium',
        totalPrice: toNumber(record?.totalPrice),
        quotedPrice: toNumber(record?.quotedPrice),
        dealPrice: toNumber(record?.dealPrice),
        expectedDealDate: toDateValue(record?.expectedDealDate),
        dealTime: toDateValue(record?.dealTime),
        contactNextTime: toDateValue(record?.contactNextTime),
        discountPercent: toNumber(record?.discountPercent),
        remark: record?.remark,
        currentBlockers: record?.currentBlockers,
        decisionMakerStatus: record?.decisionMakerStatus,
        budgetStatus: record?.budgetStatus,
        competitorInfo: record?.competitorInfo,
        riskTags: parseRiskTagsFromString(record?.riskTags),
        tagUuids: ((record as Record<string, any>)?.tags || []).map(
          (tag: { uuid: string }) => tag.uuid,
        ),
      });
      setInitialReferrals(referrals);
      const sourceRows = (profile?.related?.products?.list || []).map(
        (item: any, index: number) => ({
          key: String(item?.uuid || item?.offeringVersionUuid || `${index}`),
          uuid: String(item?.uuid || '').trim() || undefined,
          offeringUuid: item?.offeringUuid,
          offeringVersionUuid: item?.offeringVersionUuid,
          productName: item?.productName,
          productCode: item?.productCode,
          productType: item?.productType,
          productUnit: item?.productUnit,
          versionNo: item?.versionNo,
          standardPrice: String(item?.standardPrice ?? '0'),
          selectionSnapshot: item?.selectionSnapshot,
          opportunityPrice: String(item?.opportunityPrice ?? '0'),
          quantity: String(item?.quantity ?? '1'),
          totalPrice: String(item?.totalPrice ?? '0'),
          rowSort: item?.rowSort,
          remark: item?.remark,
        }),
      );
      const rows = normalizeOpportunitySharedRows(
        sourceRows.map((item) => fromOpportunityApiRow(item)),
      );
      setProductRows(rows);
      setExistingProductRows(
        sourceRows.map((item) => ({
          ...fromOpportunityApiRow(item),
          ...toOpportunityProductMeta(item),
        })),
      );
      setProductRowMeta(
        sourceRows.reduce<Record<string, OpportunityProductRowMeta>>(
          (acc, item) => {
            acc[String(item.key)] = toOpportunityProductMeta(item);
            return acc;
          },
          {},
        ),
      );
      await loadMembers('', String(record?.ownerUserUuid || ''));
      await loadRelationOptions(undefined, {
        customerUuid: String(record?.customerUuid || ''),
        pipelineUuid: String(record?.pipelineUuid || ''),
        pipelineName: String(record?.pipelineName || ''),
      });
      // 区域选项加载完成后，再设置 areas 值，确保 Cascader 可以正确回显
      form.setFieldValue('areas', record?.areas || []);
    } catch {
      setDetailLoadFailed(true);
      message.error('商机详情加载失败，请稍后重试');
    }
  };

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    if (!opportunityUuid) {
      form.resetFields();
      form.setFieldsValue({
        ...context,
        ...initialValues,
        ownerUserUuid:
          initialValues?.ownerUserUuid ||
          context?.ownerUserUuid ||
          currentMemberUuid ||
          getCurrentMemberUuid() ||
          undefined,
        areas: initialValues?.areas || [],
        priority: initialValues?.priority || 'medium',
        referrals: [],
      });
      setInitialReferrals([]);
      void loadMembers(
        '',
        String(
          initialValues?.ownerUserUuid ||
            context?.ownerUserUuid ||
            currentMemberUuid ||
            getCurrentMemberUuid() ||
            '',
        ),
      );
      void loadRelationOptions(undefined, {
        customerUuid: String(
          initialValues?.customerUuid || context?.customerUuid || '',
        ),
        pipelineUuid: String(initialValues?.pipelineUuid || ''),
      });
      setProductRows([]);
      setExistingProductRows([]);
      setProductRowMeta({});
      return;
    }
    setDetailLoadFailed(false);
    void loadEditData();
  }, [context, currentMemberUuid, form, initialValues, open, opportunityUuid]);

  const syncProducts = async (targetOpportunityUuid: string) => {
    const nextRows = normalizeOpportunitySharedRows(productRows).filter(
      (item) =>
        String(item.offeringVersionUuid || '').trim() &&
        toNumber(item.quantity) > 0,
    );
    const existingByUuid = new Map(
      existingProductRows
        .filter((item) => item.uuid)
        .map((item) => [String(item.uuid), item]),
    );
    const nextUuidSet = new Set(
      nextRows
        .map((item) => String(productRowMeta[item.key]?.uuid || '').trim())
        .filter(Boolean),
    );

    await Promise.all(
      existingProductRows
        .filter((item) => item.uuid && !nextUuidSet.has(String(item.uuid)))
        .map((item) =>
          request(`/bff/zy-crm/opportunity-products/${item.uuid}`, {
            method: 'DELETE',
          }),
        ),
    );

    for (const row of nextRows) {
      const payload = buildProductPayload(row, targetOpportunityUuid);
      const rowUuid = String(productRowMeta[row.key]?.uuid || '').trim();
      if (rowUuid && existingByUuid.has(rowUuid)) {
        await request(`/bff/zy-crm/opportunity-products/${rowUuid}`, {
          method: 'PATCH',
          data: payload,
        });
        continue;
      }
      await request('/bff/zy-crm/opportunity-products', {
        method: 'POST',
        data: payload,
      });
    }
  };

  const handleSubmit = async (continueCreating = false) => {
    if (memberLoadFailed) {
      message.error('成员列表加载失败，请稍后重试');
      return;
    }
    if (editingProductKey) {
      message.error('请先保存当前编辑中的产品');
      return;
    }
    if (!opportunityUuid && pipelineConfigMissing) {
      message.error('未配置商机状态组，暂时不能新建商机');
      return;
    }
    const values = await form.validateFields();
    return duplicateCheck.submit(values, async () => {
      const payload = {
        name: String(values.name || '').trim(),
        customerUuid: values.customerUuid,
        ownerUserUuid: values.ownerUserUuid || undefined,
        pipelineUuid: values.pipelineUuid || undefined,
        stageUuid: values.stageUuid || undefined,
        priority: values.priority || 'medium',
        areas: values.areas || [],
        totalPrice: Number(values.totalPrice || 0),
        quotedPrice:
          values.quotedPrice === undefined || values.quotedPrice === null
            ? undefined
            : Number(values.quotedPrice),
        dealPrice:
          values.dealPrice === undefined || values.dealPrice === null
            ? undefined
            : Number(values.dealPrice),
        expectedDealDate: toApiDateTime(values.expectedDealDate),
        dealTime: toApiDateTime(values.dealTime),
        contactNextTime: toApiDateTime(values.contactNextTime),
        discountPercent: isEdit
          ? values.discountPercent === undefined ||
            values.discountPercent === null
            ? undefined
            : Number(values.discountPercent)
          : undefined,
        remark: String(values.remark || '').trim() || undefined,
        followUpStatus: true,
        currentBlockers:
          String(values.currentBlockers || '').trim() || undefined,
        decisionMakerStatus: values.decisionMakerStatus || undefined,
        budgetStatus: values.budgetStatus || undefined,
        competitorInfo: String(values.competitorInfo || '').trim() || undefined,
        riskTags:
          Array.isArray(values.riskTags) && values.riskTags.length
            ? values.riskTags.join('\n')
            : undefined,
      };

      setSubmitting(true);
      try {
        let savedRecord: Pick<OpportunityItem, 'uuid' | 'name'> | undefined;
        let targetOpportunityUuid = opportunityUuid;
        if (targetOpportunityUuid) {
          await request(`/bff/zy-crm/opportunities/${targetOpportunityUuid}`, {
            method: 'PATCH',
            data: payload,
          });
        } else {
          const created = await request<
            | OpportunityItem
            | { data?: OpportunityItem; result?: OpportunityItem }
          >('/bff/zy-crm/opportunities', {
            method: 'POST',
            data: payload,
          });
          targetOpportunityUuid =
            String(
              (created as Record<string, any>)?.uuid ||
                (created as Record<string, any>)?.data?.uuid ||
                (created as Record<string, any>)?.result?.uuid ||
                '',
            ).trim() || undefined;
          const record = created as OpportunityItem & {
            data?: OpportunityItem;
            result?: OpportunityItem;
          };
          if (targetOpportunityUuid) {
            savedRecord = {
              uuid: targetOpportunityUuid,
              name:
                record.name ||
                record.data?.name ||
                record.result?.name ||
                payload.name,
            };
          }
        }
        if (!targetOpportunityUuid) {
          throw new Error('missing opportunity uuid');
        }
        await syncProducts(targetOpportunityUuid);
        try {
          await syncTargetReferrals({
            targetType: 'opportunity',
            targetUuid: targetOpportunityUuid,
            values: form.getFieldValue('referrals') || values.referrals,
            initialItems: initialReferrals,
          });
        } catch {
          message.error('商机已保存，但介绍人关系保存失败，请重试');
          return;
        }
        try {
          await replaceEntityTags(
            'opportunity',
            targetOpportunityUuid,
            values.tagUuids || [],
          );
        } catch {
          message.warning('商机主体已保存、标签未保存，请重新打开后重试');
        }
        if (savedRecord) onCreated?.(savedRecord);
        message.success(opportunityUuid ? '商机已更新' : '商机已创建');
        if (!opportunityUuid && continueCreating) {
          resetState();
          form.setFieldsValue({
            ...context,
            ...initialValues,
            ownerUserUuid:
              initialValues?.ownerUserUuid ||
              context?.ownerUserUuid ||
              currentMemberUuid ||
              getCurrentMemberUuid() ||
              undefined,
            referrals: [],
            areas: [],
            priority: 'medium',
            currentBlockers: undefined,
            decisionMakerStatus: undefined,
            budgetStatus: undefined,
            competitorInfo: undefined,
            riskTags: [],
          });
          return;
        }
        onOpenChange(false);
        await onSuccess(savedRecord);
        resetState();
      } finally {
        setSubmitting(false);
      }
    });
  };

  const editableProductRows = useMemo<OpportunityProductDraftRow[]>(() => {
    const rows = normalizeOpportunitySharedRows(productRows);
    if (editingProductKey === NEW_PRODUCT_ROW_KEY && productDraftRow) {
      return [
        ...rows,
        {
          ...normalizeOpportunitySharedRow(productDraftRow),
          ...productDraftRow,
        },
      ];
    }
    return rows.map((item) =>
      productDraftRow && item.key === editingProductKey
        ? {
            ...normalizeOpportunitySharedRow(productDraftRow),
            ...productDraftRow,
          }
        : { ...item, ...(productRowMeta[item.key] || {}) },
    );
  }, [editingProductKey, productDraftRow, productRowMeta, productRows]);

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      className={`${modalStyles.modal} ${modalStyles.form}`}
      centered
      title={opportunityUuid ? '编辑商机' : '新建商机'}
      open={open}
      width={modalWidth}
      zIndex={zIndex}
      onCancel={() => onOpenChange(false)}
      onOk={() => void handleSubmit(false)}
      okText="保存"
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          {!opportunityUuid ? (
            <Button
              disabled={detailLoadFailed || memberLoadFailed}
              loading={submitting || duplicateCheck.busy}
              onClick={() => void handleSubmit(true)}
            >
              保存并继续新增
            </Button>
          ) : null}
          <OkBtn />
        </>
      )}
      confirmLoading={submitting || duplicateCheck.busy}
      forceRender
      styles={{
        body: {
          overflowY: 'auto',
        },
      }}
    >
      <Form
        className={modalStyles.formGrid}
        form={form}
        layout="vertical"
        colon={false}
      >
        {/* 第 1 区：基础信息 */}
        <FormSectionDivider title="基础信息" />
        <Row gutter={12}>
          <Col data-crm-field="medium" span={12}>
            <Form.Item
              label="商机名称"
              name="name"
              rules={[{ required: true, message: '请输入商机名称' }]}
            >
              <Input placeholder="请输入商机名称" />
            </Form.Item>
            {duplicateCheck.hint}
          </Col>
          <Col data-crm-field="full" span={24}>
            <Form.Item label="区域" name="areas">
              <ResourceAreaSelector
                options={areaOptions}
                uuidPathMap={areaUuidPathMap}
                disabled={relationLoading}
                placeholder="请选择区域"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="short" span={12}>
            <Form.Item
              label="负责人"
              name="ownerUserUuid"
              rules={[{ required: true, message: '请选择负责人' }]}
            >
              <MemberPickerField
                adapter={ownerMemberAdapter}
                placeholder="请选择负责人"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item
              label="关联客户"
              rules={[{ required: true, message: '请选择客户' }]}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Form.Item name="customerUuid" noStyle>
                  <Select
                    showSearch
                    filterOption={false}
                    allowClear
                    options={customerOptions}
                    disabled={lockCustomer}
                    loading={relationLoading}
                    onSearch={(keyword) =>
                      void loadRelationOptions(keyword, undefined, 'customer')
                    }
                    placeholder="请选择客户"
                    style={{ flex: 1 }}
                  />
                </Form.Item>
                <Tooltip title="新增客户">
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    style={{ flexShrink: 0 }}
                    disabled={lockCustomer}
                    onClick={() => setCustomerCreateOpen(true)}
                  />
                </Tooltip>
              </div>
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item
              label="商机状态组"
              name="pipelineUuid"
              rules={[{ required: true, message: '请选择商机状态组' }]}
            >
              <Select
                showSearch
                filterOption={false}
                allowClear
                options={pipelineOptions}
                disabled={!opportunityUuid && pipelineConfigMissing}
                loading={relationLoading}
                onSearch={(keyword) =>
                  void loadRelationOptions(keyword, undefined, 'pipeline')
                }
                placeholder={
                  !opportunityUuid && pipelineConfigMissing
                    ? '请先在商机管道中配置状态组'
                    : '请选择商机状态组'
                }
                notFoundContent={
                  !opportunityUuid && pipelineConfigMissing
                    ? '未配置商机状态组'
                    : undefined
                }
                onChange={(value) => {
                  form.setFieldValue('stageUuid', undefined);
                  setStageOptions([]);
                  if (value) {
                    void loadStageOptions(value);
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item label="商机阶段" name="stageUuid">
              <Select
                showSearch
                filterOption={false}
                allowClear
                options={stageOptions}
                loading={stageLoading}
                disabled={!form.getFieldValue('pipelineUuid')}
                onSearch={(_keyword) => {
                  const pipelineUuid = form.getFieldValue('pipelineUuid');
                  if (pipelineUuid) {
                    void loadStageOptions(String(pipelineUuid));
                  }
                }}
                placeholder="请选择商机阶段"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item
              label="商机优先级"
              name="priority"
              rules={[{ required: true, message: '请选择商机优先级' }]}
            >
              <Select
                options={OPPORTUNITY_PRIORITY_OPTIONS}
                placeholder="请选择商机优先级"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item label="预计成交日期" name="expectedDealDate">
              <DatePicker
                style={{ width: '100%' }}
                showTime
                placeholder="请选择预计成交日期"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item label="实际成交日期" name="dealTime">
              <DatePicker
                style={{ width: '100%' }}
                showTime
                placeholder="请选择实际成交日期"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 第 2 区：金额与日期 */}
        <FormSectionDivider title="金额与日期" />
        <Row gutter={12}>
          <Col data-crm-field="short" span={8}>
            <Form.Item label="预计金额">
              <div style={{ display: 'flex', gap: 0 }}>
                <Form.Item name="totalPrice" noStyle>
                  <InputNumber<number>
                    min={0}
                    precision={2}
                    style={{ flex: 1, width: '100%' }}
                    placeholder="请输入预计金额"
                    formatter={(value) =>
                      value === undefined || value === null
                        ? ''
                        : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) => Number(value?.replace(/,/g, '') || 0)}
                  />
                </Form.Item>
                <Tooltip title="从报价金额复制">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    style={{ flexShrink: 0 }}
                    onClick={() => {
                      const v = form.getFieldValue('quotedPrice');
                      if (v !== undefined && v !== null) {
                        form.setFieldsValue({ totalPrice: v });
                      }
                    }}
                  />
                </Tooltip>
              </div>
            </Form.Item>
          </Col>
          <Col data-crm-field="short" span={8}>
            <Form.Item label="报价金额">
              <div style={{ display: 'flex', gap: 0 }}>
                <Form.Item name="quotedPrice" noStyle>
                  <InputNumber<number>
                    min={0}
                    precision={2}
                    style={{ flex: 1, width: '100%' }}
                    placeholder="请输入报价金额"
                    formatter={(value) =>
                      value === undefined || value === null
                        ? ''
                        : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) => Number(value?.replace(/,/g, '') || 0)}
                  />
                </Form.Item>
                <Tooltip title="从预计金额复制">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    style={{ flexShrink: 0 }}
                    onClick={() => {
                      const v = form.getFieldValue('totalPrice');
                      if (v !== undefined && v !== null) {
                        form.setFieldsValue({ quotedPrice: v });
                      }
                    }}
                  />
                </Tooltip>
              </div>
            </Form.Item>
          </Col>
          <Col data-crm-field="short" span={8}>
            <Form.Item label="成交金额">
              <div style={{ display: 'flex', gap: 0 }}>
                <Form.Item name="dealPrice" noStyle>
                  <InputNumber<number>
                    min={0}
                    precision={2}
                    style={{ flex: 1, width: '100%' }}
                    placeholder="请输入成交金额"
                    formatter={(value) =>
                      value === undefined || value === null
                        ? ''
                        : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) => Number(value?.replace(/,/g, '') || 0)}
                  />
                </Form.Item>
                <Tooltip title="从报价金额复制">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    style={{ flexShrink: 0 }}
                    onClick={() => {
                      const v = form.getFieldValue('quotedPrice');
                      if (v !== undefined && v !== null) {
                        form.setFieldsValue({ dealPrice: v });
                      }
                    }}
                  />
                </Tooltip>
              </div>
            </Form.Item>
          </Col>
          <Col data-crm-field="short" span={12}>
            <Form.Item label="下次联系时间" name="contactNextTime">
              <DatePicker
                style={{ width: '100%' }}
                placeholder="请选择下次联系时间"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 第 3 区：需求与决策 */}
        <FormSectionDivider title="需求与决策" />
        <Row gutter={12}>
          <Col data-crm-field="full" span={24}>
            <Form.Item label="客户痛点" name="currentBlockers">
              <Input.TextArea
                rows={2}
                showCount
                maxLength={500}
                placeholder="请输入客户痛点或当前阻塞点"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item label="预算情况" name="budgetStatus">
              <Select
                allowClear
                options={BUDGET_STATUS_OPTIONS}
                placeholder="请选择预算情况"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" span={12}>
            <Form.Item label="决策人状态" name="decisionMakerStatus">
              <Select
                allowClear
                options={DECISION_MAKER_STATUS_OPTIONS}
                placeholder="请选择决策人状态"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="full" span={24}>
            <Form.Item label="竞争对手" name="competitorInfo">
              <Input.TextArea
                rows={2}
                showCount
                maxLength={500}
                placeholder="请输入竞争对手信息"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="full" span={24}>
            <Form.Item label="风险标签" name="riskTags">
              <Select
                mode="tags"
                placeholder="输入风险标签后按回车添加"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="标签" name="tagUuids">
              <CrmTagSelector entityType="opportunity" canCreate />
            </Form.Item>
          </Col>
        </Row>

        {/* 第 4 区：备注 */}
        <FormSectionDivider title="备注" />
        <ReferralFormSection
          form={form}
          name="referrals"
          title="介绍人关系"
          initialItems={initialReferrals}
        />
        <Form.Item label="备注" name="remark">
          <Input.TextArea
            rows={3}
            showCount
            maxLength={500}
            placeholder="请输入备注"
          />
        </Form.Item>

        {/* 产品明细 + 金额汇总 */}
        {
          <>
            <ProductCenterFormSection
              title="产品明细"
              rows={editableProductRows}
              editingRowKey={editingProductKey}
              onBeginEdit={beginEditProductRow}
              onSaveEdit={saveProductRow}
              onCancelEdit={stopProductEditing}
              onDraftChange={handleProductDraftChange}
              onDeleteRow={deleteProductRow}
              productCenter={productCenter}
              transactionPrice="opportunityPrice"
              transactionPriceTitle="商机价"
              onProductSelected={handleProductSelected}
              scroll={{ x: 1100 }}
              addButtonText="新增一条产品"
              onAdd={addProductRow}
            />

            <Form.Item label="整单折扣" name="discountPercent">
              <InputNumber min={0} precision={2} style={{ width: 200 }} />
            </Form.Item>
          </>
        }

        {detailLoadFailed ? (
          <Button type="link" onClick={() => onOpenChange(false)}>
            当前详情加载失败，请关闭后重试
          </Button>
        ) : null}
      </Form>
      {customerCreateOpen ? (
        <CustomerEditModal
          open={customerCreateOpen}
          onOpenChange={setCustomerCreateOpen}
          width={screens.xl ? 1080 : screens.md ? 900 : '94vw'}
          currentMemberUuid={currentMemberUuid ?? null}
          zIndex={(zIndex ?? 1000) + 100}
          onSuccess={(record) => {
            setCustomerCreateOpen(false);
            if (record?.uuid) {
              form.setFieldsValue({ customerUuid: record.uuid });
              void loadRelationOptions(undefined, {
                customerUuid: record.uuid,
              });
            }
          }}
        />
      ) : null}
    </Modal>
  );
}
